import { useAPIMutation } from "@/api/api";
import {
  IFSigninAccountReq,
  IFSigninPhoneReq,
} from "@/api/apiReq/IFSigninReq";
import { processMobilePrefix } from "@/utils/mobileUtils";
import { LoginPopInfo } from "@/api/apiRsp/IFActivityList";
import { IFEmpty } from "@/api/apiRsp/IFEmpty";
import { IFSigninRsp } from "@/api/apiRsp/IFSigninRsp";
import NiceModal from "@ebay/nice-modal-react";
import { getTimestamp } from "@/Core/utils";
import LoginPopup from "@/pages/LoginPopup";
import { feedback } from "@/utils/feedback";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "./commonStore";
import { GUIDE_STEP, sys } from "@/Core/utils/system";
import { trackEmEvent, syncEmUserFromAuth } from "@/features/analytics/em/sdk";
import { getCurrentCountryCode } from "@/utils/countryConfigUtils";

const resolveLoginAuthType = (
  loginType?: number,
  fallback: "account" | "phone" = "account",
) => {
  if (loginType === 0) {
    return "phone";
  }

  if (loginType === 1) {
    return "email";
  }

  if (loginType === 2) {
    return "account";
  }

  return fallback;
};

const buildLoginTrackProps = (
  params: Partial<IFSigninAccountReq & IFSigninPhoneReq>,
  fallback: "account" | "phone" = "account",
) => ({
  authType: resolveLoginAuthType(params.loginType, fallback),
  countryCode: (getCurrentCountryCode() || "").toLowerCase(),
  hasInviteCode: !!params.inviteCode,
  hasImageCode: !!params.code,
});

const getErrorCode = (error: any): string => {
  return String(error?.code || error?.errorCode || "unknown");
};

export const useLoginPopup = () => {
  const { isLogin } = useGlobalState();
  const { trigger } = useAPIMutation<LoginPopInfo[], IFEmpty>({
    path: "api/index/loginPop",
    type: "get",
  });
  useEffect(() => {
    if (isLogin) {
      trigger({}).then((res) => {
        let index = 0;
        const show = (data: LoginPopInfo) => {
          if (!data) {
            return;
          }
          NiceModal.show(LoginPopup, data).then(() => {
            index += 1;
            show(res[index]);
          });
        };
        show(res[index]);
      });
    }
  }, [isLogin]);
};

/**
 * 账号登录
 */
export function useSiginAccount() {
  const { setToken, setTokenTime } = useGlobalState();

  const { trigger: originalTrigger, data, error, isMutating } = useAPIMutation<
    IFSigninRsp,
    IFSigninAccountReq
  >({ path: "api/login/account", type: "post" });
  const { t } = useTranslation();

  const trigger = (params: IFSigninAccountReq) => {
    const trackProps = buildLoginTrackProps(params, "account");
    trackEmEvent("auth_login_submit", trackProps);

    return originalTrigger(params)
      .then((res) => {
        trackEmEvent("auth_login_success", trackProps);
        syncEmUserFromAuth(res?.userId, res?.userInfo);
        return res;
      })
      .catch((triggerError) => {
        trackEmEvent("auth_login_fail", {
          ...trackProps,
          errorCode: getErrorCode(triggerError),
        });
        throw triggerError;
      });
  };

  useEffect(() => {
    if (data) {
      setToken(data?.token);
      setTokenTime(getTimestamp().toString());
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      feedback.error(`${t(error?.code)}`);
    }
  }, [error]);

  return {
    trigger,
    errorCode: error?.code,
    isMutating,
  };
}

/**
 * 手机号登录
 */
export function useSiginPhone() {
  const { setToken, setTokenTime } = useGlobalState();
  const {
    trigger: originalTrigger,
    data,
    error,
    isMutating,
  } = useAPIMutation<IFSigninRsp, IFSigninPhoneReq>({
    path: "api/login/account",
    type: "post",
  });
  const { t } = useTranslation();

  const trigger = (params: IFSigninPhoneReq) => {
    // 使用统一的手机号处理方法
    const processedParams = {
      ...params,
      username: params.username ? processMobilePrefix(params.username) : params.username,
    };
    const trackProps = buildLoginTrackProps(processedParams, "phone");
    trackEmEvent("auth_login_submit", trackProps);

    return originalTrigger(processedParams)
      .then((res) => {
        trackEmEvent("auth_login_success", trackProps);
        syncEmUserFromAuth(res?.userId, res?.userInfo);
        return res;
      })
      .catch((triggerError) => {
        trackEmEvent("auth_login_fail", {
          ...trackProps,
          errorCode: getErrorCode(triggerError),
        });
        throw triggerError;
      });
  };

  useEffect(() => {
    if (data) {
      setToken(data?.token);
      setTokenTime(getTimestamp().toString());
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      feedback.error(`${t(error?.code)}`);
    }
  }, [error]);
  return {
    trigger,
    data,
    errorCode: error?.code,
    isMutating,
  };
}

/**
 * 会员登出
 */

export function useSignOut() {
  const { trigger } = useAPIMutation<IFEmpty, IFEmpty>({
    path: "/api/member/logout",
    type: "post",
  });

  return {
    trigger,
  };
}
